'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  User,
  Settings,
  History,
  Bookmark,
  LogOut,
  Edit3,
  Save,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
  Trash2,
  Bell,
  Shield,
  ChevronRight,
  IndianRupee,
  Briefcase,
  Home,
  GraduationCap,
  Car,
  Gem,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import type { LoanCategory } from '@/lib/mockData';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApplicationRecord {
  id: string;
  aggregatorName: string;
  logoInitials: string;
  logoColor: string;
  loanType: LoanCategory;
  appliedAt: string;
  status: 'Applied' | 'Under Review' | 'Approved' | 'Rejected';
  amount: string;
}

interface SavedAggregator {
  id: string; // user_saved_aggregators.id
  aggregatorId: string;
  name: string;
  logoInitials: string;
  logoColor: string;
  category: LoanCategory;
  interestRateMin: number;
  interestRateMax: number;
  referralUrl: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_LOAN_TYPES: { label: LoanCategory; icon: React.ReactNode; color: string }[] = [
  { label: 'Personal Loan', icon: <IndianRupee size={16} />, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { label: 'Home Loan', icon: <Home size={16} />, color: 'bg-green-50 text-green-700 border-green-200' },
  { label: 'Business Loan', icon: <Briefcase size={16} />, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { label: 'Education Loan', icon: <GraduationCap size={16} />, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { label: 'Gold Loan', icon: <Gem size={16} />, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { label: 'Vehicle Loan', icon: <Car size={16} />, color: 'bg-rose-50 text-rose-700 border-rose-200' },
];

const STATUS_CONFIG: Record<ApplicationRecord['status'], { label: string; icon: React.ReactNode; className: string }> = {
  Applied: {
    label: 'Applied',
    icon: <Clock size={12} />,
    className: 'bg-blue-50 text-blue-600 border border-blue-200',
  },
  'Under Review': {
    label: 'Under Review',
    icon: <Clock size={12} />,
    className: 'bg-amber-50 text-amber-600 border border-amber-200',
  },
  Approved: {
    label: 'Approved',
    icon: <CheckCircle2 size={12} />,
    className: 'bg-green-50 text-green-600 border border-green-200',
  },
  Rejected: {
    label: 'Rejected',
    icon: <XCircle size={12} />,
    className: 'bg-red-50 text-red-500 border border-red-200',
  },
};

type TabId = 'settings' | 'history' | 'saved';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'settings', label: 'Account Settings', icon: <Settings size={16} /> },
  { id: 'history', label: 'Application History', icon: <History size={16} /> },
  { id: 'saved', label: 'Saved Aggregators', icon: <Bookmark size={16} /> },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProfileAvatar({ user, size = 'lg' }: { user: any; size?: 'sm' | 'lg' }) {
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  const sizeClass = size === 'lg' ? 'w-20 h-20 text-2xl' : 'w-10 h-10 text-sm';

  if (user?.user_metadata?.avatar_url) {
    return (
      <img
        src={user.user_metadata.avatar_url}
        alt={displayName}
        className={`${sizeClass} rounded-full object-cover ring-4 ring-white shadow-md`}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded-full bg-primary flex items-center justify-center text-white font-bold ring-4 ring-white shadow-md`}>
      {initials}
    </div>
  );
}

function SettingsTab({ user }: { user: any }) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(
    user?.user_metadata?.full_name || user?.email?.split('@')[0] || ''
  );
  const [notifications, setNotifications] = useState({ email: true, sms: false, offers: true });

  const handleSave = () => {
    setEditing(false);
    toast.success('Profile updated successfully');
  };

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between mb-5">
          <h3 className="text-base font-semibold text-foreground">Profile Information</h3>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
            >
              <Edit3 size={14} /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(false)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <X size={14} /> Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 text-sm bg-primary text-white font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-5 mb-6">
          <ProfileAvatar user={user} size="lg" />
          <div>
            <p className="text-lg font-bold text-foreground">
              {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
            </p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              <CheckCircle2 size={11} /> Verified
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-base">Display Name</label>
            {editing ? (
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input-base"
                placeholder="Your name"
              />
            ) : (
              <p className="text-sm text-foreground bg-muted/50 rounded-xl px-4 py-2.5 border border-border">
                {displayName || '—'}
              </p>
            )}
          </div>
          <div>
            <label className="label-base">Email Address</label>
            <p className="text-sm text-foreground bg-muted/50 rounded-xl px-4 py-2.5 border border-border text-muted-foreground">
              {user?.email || '—'}
            </p>
          </div>
          <div>
            <label className="label-base">Account Type</label>
            <p className="text-sm text-foreground bg-muted/50 rounded-xl px-4 py-2.5 border border-border">
              {user?.app_metadata?.provider === 'google' ? 'Google Account' : 'Email Account'}
            </p>
          </div>
          <div>
            <label className="label-base">Member Since</label>
            <p className="text-sm text-foreground bg-muted/50 rounded-xl px-4 py-2.5 border border-border">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' }) : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-5">
          <Bell size={16} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Notification Preferences</h3>
        </div>
        <div className="space-y-4">
          {[
            { key: 'email', label: 'Email Notifications', desc: 'Loan updates and application status' },
            { key: 'sms', label: 'SMS Alerts', desc: 'Important alerts via text message' },
            { key: 'offers', label: 'Loan Offers & Deals', desc: 'New offers and promotional rates' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-border last:border-0">
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
              <button
                onClick={() => setNotifications((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                  notifications[key as keyof typeof notifications] ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    notifications[key as keyof typeof notifications] ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-5">
          <Shield size={16} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Security</h3>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Two-Factor Authentication', desc: 'Add an extra layer of security', action: 'Enable' },
            { label: 'Active Sessions', desc: 'Manage devices where you are logged in', action: 'View' },
          ].map(({ label, desc, action }) => (
            <button
              key={label}
              onClick={() => toast.info('This feature is coming soon')}
              className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/60 transition-colors group"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                {action}
                <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function HistoryTab({ userId }: { userId: string }) {
  const [filter, setFilter] = useState<'All' | ApplicationRecord['status']>('All');
  const [applications, setApplications] = useState<ApplicationRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('user_loan_applications')
          .select(`
            id,
            loan_type,
            amount,
            status,
            applied_at,
            aggregators (
              id,
              name,
              logo_initials,
              logo_color
            )
          `)
          .eq('user_id', userId)
          .order('applied_at', { ascending: false });

        if (error) {
          console.log('Fetch applications error:', error.message);
          setApplications([]);
          return;
        }

        const mapped: ApplicationRecord[] = (data || []).map((row: any) => ({
          id: row.id,
          aggregatorName: row.aggregators?.name || 'Unknown',
          logoInitials: row.aggregators?.logo_initials || '??',
          logoColor: row.aggregators?.logo_color || '#6B7280',
          loanType: row.loan_type as LoanCategory,
          appliedAt: row.applied_at,
          status: row.status as ApplicationRecord['status'],
          amount: row.amount,
        }));
        setApplications(mapped);
      } catch (err) {
        console.log('Applications fetch failed:', err);
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchApplications();
  }, [userId]);

  const filtered = filter === 'All'
    ? applications
    : applications.filter((a) => a.status === filter);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-2xl border border-border p-4 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-1/3" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
              <div className="h-6 bg-muted rounded-full w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {(['All', 'Applied', 'Under Review', 'Approved', 'Rejected'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === s
                ? 'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center">
          <History size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">
            {applications.length === 0 ? 'No loan applications yet' : 'No applications match this filter'}
          </p>
          {applications.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Applications you submit via the directory will appear here
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((app) => {
            const statusCfg = STATUS_CONFIG[app.status];
            return (
              <div
                key={app.id}
                className="bg-card rounded-2xl border border-border p-4 flex items-center gap-4 hover:shadow-sm transition-shadow"
              >
                {/* Logo */}
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: app.logoColor }}
                >
                  {app.logoInitials}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{app.aggregatorName}</p>
                    <span className="badge-category">{app.loanType}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <p className="text-xs text-muted-foreground">
                      Applied {new Date(app.appliedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    <span className="text-xs font-semibold text-foreground">{app.amount}</span>
                  </div>
                </div>

                {/* Status */}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${statusCfg.className}`}>
                  {statusCfg.icon}
                  {statusCfg.label}
                </span>

                {/* Action */}
                <button
                  onClick={() => toast.info('Redirecting to aggregator...')}
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                  title="View aggregator"
                >
                  <ExternalLink size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center pt-1">
        Showing {filtered.length} of {applications.length} applications
      </p>
    </div>
  );
}

function SavedAggregatorsTab({ userId }: { userId: string }) {
  const [savedAggregators, setSavedAggregators] = useState<SavedAggregator[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSaved = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('user_saved_aggregators')
        .select(`
          id,
          aggregator_id,
          aggregators (
            id,
            name,
            logo_initials,
            logo_color,
            category,
            interest_rate_min,
            interest_rate_max,
            referral_url
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.log('Fetch saved aggregators error:', error.message);
        setSavedAggregators([]);
        return;
      }

      const mapped: SavedAggregator[] = (data || []).map((row: any) => ({
        id: row.id,
        aggregatorId: row.aggregator_id,
        name: row.aggregators?.name || 'Unknown',
        logoInitials: row.aggregators?.logo_initials || '??',
        logoColor: row.aggregators?.logo_color || '#6B7280',
        category: row.aggregators?.category as LoanCategory,
        interestRateMin: row.aggregators?.interest_rate_min || 0,
        interestRateMax: row.aggregators?.interest_rate_max || 0,
        referralUrl: row.aggregators?.referral_url || '#',
      }));
      setSavedAggregators(mapped);
    } catch (err) {
      console.log('Saved aggregators fetch failed:', err);
      setSavedAggregators([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchSaved();
  }, [userId, fetchSaved]);

  const handleRemove = async (savedId: string, name: string) => {
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('user_saved_aggregators')
        .delete()
        .eq('id', savedId);

      if (error) {
        console.log('Remove saved aggregator error:', error.message);
        toast.error('Failed to remove aggregator');
        return;
      }

      setSavedAggregators((prev) => prev.filter((a) => a.id !== savedId));
      toast.success(`Removed ${name} from saved`);
    } catch (err) {
      toast.error('Failed to remove aggregator');
    }
  };

  const handleRemoveAll = async () => {
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('user_saved_aggregators')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.log('Remove all saved error:', error.message);
        toast.error('Failed to clear saved aggregators');
        return;
      }

      setSavedAggregators([]);
      toast.success('All saved aggregators cleared');
    } catch (err) {
      toast.error('Failed to clear saved aggregators');
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card rounded-2xl border border-border p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-2/3" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-base font-semibold text-foreground">Saved Aggregators</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Loan aggregators you have bookmarked from the directory.
            </p>
          </div>
          {savedAggregators.length > 0 && (
            <button
              onClick={handleRemoveAll}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 font-medium flex-shrink-0 ml-4"
            >
              <Trash2 size={13} /> Clear all
            </button>
          )}
        </div>
      </div>

      {savedAggregators.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center">
          <Bookmark size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No saved aggregators yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Bookmark aggregators from the directory to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {savedAggregators.map((agg) => {
            const loanType = ALL_LOAN_TYPES.find((t) => t.label === agg.category);
            return (
              <div
                key={agg.id}
                className="bg-card rounded-2xl border border-border p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
              >
                {/* Logo */}
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: agg.logoColor }}
                >
                  {agg.logoInitials}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{agg.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${loanType?.color || 'bg-muted text-muted-foreground border-border'}`}>
                      {loanType?.icon}
                      {agg.category}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {agg.interestRateMin}% – {agg.interestRateMax}% p.a.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <a
                    href={agg.referralUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                    title="Visit aggregator"
                  >
                    <ExternalLink size={14} />
                  </a>
                  <button
                    onClick={() => handleRemove(agg.id, agg.name)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                    title="Remove from saved"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {savedAggregators.length > 0 && (
        <div className="bg-secondary rounded-2xl border border-primary/20 p-4 flex items-center gap-3">
          <Bookmark size={18} className="text-primary flex-shrink-0" />
          <p className="text-sm text-secondary-foreground">
            <span className="font-semibold">{savedAggregators.length} aggregator{savedAggregators.length > 1 ? 's' : ''} saved</span>
            {' — '}
            {savedAggregators.map((a) => a.name).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AccountPage() {
  const router = useRouter();
  const { user, loading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('settings');

  useEffect(() => {
    if (!loading && !user) {
      router.push('/');
    }
  }, [user, loading, router]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      router.push('/');
    } catch {
      toast.error('Failed to sign out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="page-title">My Account</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your profile, track loan applications, and view saved aggregators.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              {/* Profile summary */}
              <div className="p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <ProfileAvatar user={user} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Nav tabs */}
              <nav className="p-2">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                      activeTab === tab.id
                        ? 'bg-secondary text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>

              {/* Sign out */}
              <div className="p-2 border-t border-border mt-1">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'settings' && <SettingsTab user={user} />}
            {activeTab === 'history' && <HistoryTab userId={user.id} />}
            {activeTab === 'saved' && <SavedAggregatorsTab userId={user.id} />}
          </div>
        </div>
      </main>
    </div>
  );
}
